import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { api } from '@/api/client';
import { SortableTh, SortDir } from '@/components/SortableTh';

interface PadronRow {
  dni: string;
  op_canonica: string;
  apellido_paterno: string | null;
  apellido_materno: string | null;
  nombres: string | null;
  departamento: string | null;
  provincia: string | null;
  distrito: string | null;
  ubigeo: string | null;
  alianza_primarias: string;
  desafiliado: boolean;
  desaf_expediente: string | null;
}

interface Paged { total: number; page: number; page_size: number; items: PadronRow[]; }
interface Filtros { departamentos: string[]; ops: string[]; alianzas: string[]; }

export function Padron() {
  const [params] = useSearchParams();
  const [q, setQ] = useState('');
  const [op, setOp] = useState(params.get('op_canonica') ?? '');
  const [desafiliado, setDesafiliado] = useState(params.get('desafiliado') ?? '');
  const [depto, setDepto] = useState('');
  const [alianza, setAlianza] = useState('');
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [page, setPage] = useState(1);
  const pageSize = 100;

  const onSort = (k: string, d: SortDir) => { setSortBy(d ? k : null); setSortDir(d); setPage(1); };

  const { data: filtros } = useQuery<Filtros>({
    queryKey: ['padron-filtros'],
    queryFn: async () => (await api.get<Filtros>('/padron/filtros')).data,
    staleTime: 5 * 60 * 1000,
  });

  const { data, isLoading } = useQuery<Paged>({
    queryKey: ['padron', q, op, desafiliado, depto, alianza, sortBy, sortDir, page],
    queryFn: async () => (await api.get<Paged>('/padron', {
      params: {
        q: q || undefined,
        op_canonica: op || undefined,
        desafiliado: desafiliado === '' ? undefined : desafiliado === 'true',
        departamento: depto || undefined,
        alianza_primarias: alianza || undefined,
        sort_by: sortBy || undefined,
        sort_dir: sortBy ? sortDir : undefined,
        page, page_size: pageSize,
      },
    })).data,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.page_size)) : 1;

  return (
    <div className="p-6 space-y-4 max-w-[1400px]">
      <header>
        <h1 className="text-xl font-bold text-arpp-primary">Padrón Postulable DNROP-JNE</h1>
        <p className="text-xs text-gray-600">
          {data?.total?.toLocaleString('es-PE') ?? '…'} afiliados elegibles · fuente: Oficio N° 4172-2026-SG/JNE
        </p>
        <p className="text-[11px] text-gray-500 mt-1">
          Universo de derecho a POSTULAR (ARPP+ARPT+NINGUNA · RP+PERUP · excluye desafiliados según filtro).
        </p>
      </header>

      <div className="flex flex-wrap gap-3 items-end bg-white border rounded p-3">
        <div>
          <label className="block text-[11px] text-gray-600">Búsqueda DNI/nombre</label>
          <input value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }}
            className="border rounded px-2 py-1 text-sm w-56" placeholder="DNI o nombre/apellido" />
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">OP</label>
          <select value={op} onChange={(e) => { setPage(1); setOp(e.target.value); }} className="border rounded px-2 py-1 text-sm">
            <option value="">Ambos</option>
            <option value="RP">RP</option>
            <option value="PERUP">PERUP</option>
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Alianza primarias</label>
          <select value={alianza} onChange={(e) => { setPage(1); setAlianza(e.target.value); }} className="border rounded px-2 py-1 text-sm">
            <option value="">Todas</option>
            {filtros?.alianzas.map((a) => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Departamento</label>
          <select value={depto} onChange={(e) => { setPage(1); setDepto(e.target.value); }} className="border rounded px-2 py-1 text-sm w-44">
            <option value="">Todos</option>
            {filtros?.departamentos.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] text-gray-600">Desafiliado</label>
          <select value={desafiliado} onChange={(e) => { setPage(1); setDesafiliado(e.target.value); }} className="border rounded px-2 py-1 text-sm">
            <option value="">Todos</option>
            <option value="false">Hábiles</option>
            <option value="true">Solo desafiliados</option>
          </select>
        </div>
      </div>

      <div className="bg-white border rounded overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-xs">
            <tr>
              <SortableTh label="DNI" sortKey="dni" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Apellido pat." sortKey="apellido_paterno" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Apellido mat." sortKey="apellido_materno" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Nombres" sortKey="nombres" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="OP" sortKey="op_canonica" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={['RP', 'PERUP']} filterValue={op} onFilter={(v) => { setOp(v); setPage(1); }} />
              <SortableTh label="Depto" sortKey="departamento" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={filtros?.departamentos ?? []} filterValue={depto} onFilter={(v) => { setDepto(v); setPage(1); }} />
              <SortableTh label="Provincia" sortKey="provincia" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Distrito" sortKey="distrito" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
              <SortableTh label="Primarias" sortKey="alianza_primarias" sortBy={sortBy} sortDir={sortDir} onSort={onSort}
                filterOptions={filtros?.alianzas ?? []} filterValue={alianza} onFilter={(v) => { setAlianza(v); setPage(1); }} />
              <SortableTh label="Desafil" sortKey="desafiliado" sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td className="p-4 text-gray-500" colSpan={10}>Cargando…</td></tr>}
            {data?.items.map((p) => (
              <tr key={p.dni} className="border-t hover:bg-gray-50">
                <td className="px-2 py-1.5 font-mono text-xs">{p.dni}</td>
                <td className="px-2 py-1.5">{p.apellido_paterno ?? '—'}</td>
                <td className="px-2 py-1.5">{p.apellido_materno ?? '—'}</td>
                <td className="px-2 py-1.5">{p.nombres ?? '—'}</td>
                <td className="px-2 py-1.5"><span className={`text-[11px] px-2 py-0.5 rounded ${p.op_canonica === 'RP' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{p.op_canonica}</span></td>
                <td className="px-2 py-1.5 text-xs">{p.departamento ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{p.provincia ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{p.distrito ?? '—'}</td>
                <td className="px-2 py-1.5 text-xs">{p.alianza_primarias}</td>
                <td className="px-2 py-1.5 text-xs">
                  {p.desafiliado ? <span className="text-red-700">⚠ {p.desaf_expediente ?? 'sí'}</span> : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 text-xs">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="border rounded px-3 py-1 disabled:opacity-40">←</button>
        <span>Página {page} de {totalPages.toLocaleString('es-PE')}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="border rounded px-3 py-1 disabled:opacity-40">→</button>
      </div>
    </div>
  );
}
